import type { IJobApplication } from './../../stores/JobApplicationStore/JobApplication';
import captureEvent from '../../utils/captureEvent';
import { logError } from 'src/utils/logError';
import {
  buildBaseCandidateData,
  buildHirerData,
  buildJobData,
  type CandidateData,
  type FiltersEventCaptureData,
  type HirerData,
  type JobData,
} from './buildCaptureEventData';
import type { IJobApplicationStore } from '../../stores/JobApplicationStore';
import type { IUserStore } from '../../stores/UserStore';
import type { IStore } from '../../stores/Store';
import type { IJobStore } from '../../stores/JobStore';

interface CandidateForListImpression extends CandidateData {
  pageRank: number;
}

interface CandidateListImpressionContract {
  candidate: CandidateForListImpression;
  hirer: HirerData;
  job: JobData;
  filters: FiltersEventCaptureData;
}

const eventType = 'candidate_list_impression';

const buildCandidateListImpressionCaptureData = ({
  jobApplicationStore,
  jobApplication,
  userStore,
  store,
  jobStore,
  filters,
  rank,
}: {
  jobApplicationStore: IJobApplicationStore;
  jobApplication: IJobApplication;
  userStore: IUserStore;
  store: IStore;
  jobStore: IJobStore;
  filters: FiltersEventCaptureData;
  rank: number;
}): CandidateListImpressionContract | undefined => {
  const candidate = buildBaseCandidateData({
    jobApplicationStore,
    jobApplication,
    eventType,
  });
  const hirer = buildHirerData({ store, userStore, eventType });
  const job = buildJobData({ eventType, jobStore });
  if (!job || !hirer || !candidate) {
    return;
  }

  return {
    candidate: { ...candidate, pageRank: rank },
    hirer,
    job,
    filters,
  };
};

export const sendCandidateListImpression = async ({
  jobApplicationStore,
  jobApplication,
  userStore,
  store,
  jobStore,
  filters,
  rank,
}: {
  jobApplicationStore: IJobApplicationStore;
  jobApplication: IJobApplication;
  userStore: IUserStore;
  store: IStore;
  jobStore: IJobStore;
  filters: FiltersEventCaptureData;
  rank: number;
}) => {
  const data = buildCandidateListImpressionCaptureData({
    jobApplicationStore,
    jobApplication,
    userStore,
    jobStore,
    filters,
    store,
    rank,
  });

  if (!data) {
    // dont send capture event if data not provided.
    return;
  }

  try {
    await captureEvent({
      eventType,
      data,
    });
  } catch (error: any) {
    logError(
      {
        errorCode: 'CAPTURE_EVENT',
        description: 'Error attempting to captureEvent',
      },
      {
        eventType,
        data,
        error,
      },
    );
  }

  return null;
};
